import { Cancel } from '@mui/icons-material';
import {
  Button,
  Divider,
  Drawer,
  FormControl,
  FormLabel,
  Grid2,
  TextField,
  Typography
} from '@mui/material';
import { Metadata } from '@thro-types/billing';

import { Field, Form, Formik } from 'formik';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { SaveButton } from 'app/components';
import { translations } from 'locales/i18n';

import { useMetadataSettingsInitialValue, metadataSettingsValidationSchema } from './validation';

interface MetadataTypeProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  metadata: Metadata | undefined;
  handleSaveClick: (values: any) => Promise<void>;
}

export const MetadataDrawer = (props: MetadataTypeProps) => {
  const { t } = useTranslation();
  return (
    <Drawer
      anchor="right"
      open={props.open}
      PaperProps={{
        sx: { width: { xs: '70%', md: '35%', xl: '25%' } }
      }}
      onClose={() => props.setOpen(false)}
    >
      <Formik
        initialValues={useMetadataSettingsInitialValue(props.metadata)}
        onSubmit={values => props.handleSaveClick(values)}
        validationSchema={metadataSettingsValidationSchema(t)}
        validateOnMount
      >
        {({ touched, errors, isValid }) => (
          <Form>
            <Grid2
              container
              padding={2}
              flexDirection={'column'}
              gap={2}
              height={'100vh'}
              justifyContent={'flex-end'}
              width={'100%'}
            >
              <Grid2 container size={12} gap={1} flexDirection={'column'}>
                <Typography variant="h5" fontWeight={'bold'}>
                  {t(translations.pages.billingSettings.metadata.drawer.update())}
                </Typography>
                <Divider />
              </Grid2>

              <Grid2 container direction={'column'} spacing={2}>
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.pages.billingSettings.metadata.key())}</FormLabel>
                  <Field
                    as={TextField}
                    disabled={props?.metadata?.key}
                    id="key"
                    name="key"
                    error={touched.key && Boolean(errors.key)}
                    helperText={touched.key && errors.key}
                    size="small"
                  />
                </FormControl>
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.pages.billingSettings.metadata.text())}</FormLabel>
                  <Field id="value" name="value">
                    {({ field, meta }) => {
                      return (
                        <TextField
                          {...field}
                          multiline
                          error={meta.touched && meta.error}
                          helperText={meta.touched && meta.error}
                          size="small"
                        />
                      );
                    }}
                  </Field>
                </FormControl>
              </Grid2>
              <Grid2 size={12} alignContent={'center'} marginTop={'auto'}>
                <Divider sx={{ marginLeft: 1, marginRight: 0.6, flex: 1 }} />
              </Grid2>
              <Grid2 container justifyContent={'flex-end'} gap={2} alignItems={'flex-end'}>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => props.setOpen(false)}
                >
                  {t(translations.common.cancel())}
                </Button>
                <SaveButton isSubmitting={false} isValid={isValid} />
              </Grid2>
            </Grid2>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};
